import { useReducer, useEffect } from 'react';
import { getDefaultStringParameters, formatQueryParam } from '../utils/url-utils';
import { MODULES, PLP_QUERY_PARAM_KEYS, type PlpType } from 'Data/constants';
import { removePlusSign } from 'Utils/utils';

export type QueryParamState = {
  categoryId: string;
  hid: string;
  levels: string[];
  searchText: string;
  sortBy: string;
  page: number;
  queryParam: string;
  hasParams: boolean;
  plpType: PlpType;
};

const initialState = (plpType: PlpType): QueryParamState => {
  return {
    categoryId: '', // will only receive a value on all PLP's but search PLP
    levels: [],
    queryParam: '',
    sortBy: '',
    page: 0,
    plpType,
    searchText: '', // will only receive a value on the search PLP
    hid: '',
    hasParams: false,
  };
};

export type QueryParamReducer =
  | {
      type: 'setInitialQueryParams';
      payload: {
        categoryId?: string;
        level1Category?: string;
        queryParam?: string;
        searchText?: string;
        sortBy?: string;
        page?: string | number;
      };
    }
  | { type: 'setFilter' | 'clearAllFilters'; payload: string }
  | { type: 'setPage'; payload: number }
  | { type: 'setSortBy'; payload: string };

export const usePlpQueryParams = ({ plpType }: { plpType: PlpType }) => {
  const defaultSortByParam = plpType === MODULES.PLP ? 'trending' : 'relevance';

  const [state, dispatch] = useReducer((state: QueryParamState, { type, payload }: QueryParamReducer) => {
    switch (type) {
      case 'setInitialQueryParams':
        return {
          ...state,
          ...{
            categoryId: payload.categoryId ?? '',
            levels: payload.level1Category ? [...state.levels, payload.level1Category] : state.levels,
            queryParam: formatQueryParam(payload.queryParam ?? '', payload.searchText ?? ''),
            sortBy: payload.sortBy ?? defaultSortByParam,
            page: (typeof payload.page == 'string' ? parseInt(payload.page) : payload.page) ?? 0,
            searchText: payload.searchText ? removePlusSign(payload.searchText) : '',
            hasParams: true,
          },
        };
      case 'setFilter':
        return {
          ...state,
          queryParam: formatQueryParam(payload, state.searchText),
          page: 0,
        };
      case 'clearAllFilters':
        return {
          ...state,
          queryParam: formatQueryParam(payload, state.searchText),
          page: 0,
        };
      case 'setPage':
        return {
          ...state,
          page: payload,
        };
      case 'setSortBy':
        return {
          ...state,
          sortBy: payload,
          page: 0, //should reset to first page when sort is selected
        };
      default:
        return {
          ...state,
        };
    }
  }, initialState(plpType));

  const { sortBy, page, queryParam, searchText, hasParams } = state;

  // Pull the query params from the URL to set state and trigger GQL query
  useEffect(() => {
    if (typeof window === 'object') {
      const { formattedDefaultStringParameters, categoryId, level1Category } = getDefaultStringParameters();
      dispatch({
        type: 'setInitialQueryParams',
        payload: {
          ...formattedDefaultStringParameters,
          categoryId,
          level1Category,
        },
      });
    }
  }, [plpType]);

  // Set the url to include query params without refreshing the page
  useEffect(() => {
    if (hasParams) {
      const { SORT_PARAM_KEY, PAGE_PARAM_KEY, QUERY_PARAM_KEY, SEARCH_TEXT_PARAM_KEY } = PLP_QUERY_PARAM_KEYS;

      const url = new URL(window.location.href);
      const searchParams = new URLSearchParams(url.search);

      searchParams.set(SORT_PARAM_KEY, sortBy);
      searchParams.set(PAGE_PARAM_KEY, page.toString());
      queryParam && searchParams.set(QUERY_PARAM_KEY, queryParam);
      searchText && searchParams.set(SEARCH_TEXT_PARAM_KEY, searchText);

      window.history.replaceState({}, document.title, `${location.pathname}?${searchParams.toString()}`);
    }
  }, [queryParam, sortBy, page, searchText, hasParams]);

  return {
    state,
    dispatch,
  };
};
